import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  /** Friendly label shown above the fallback (e.g. "Profile", "Photo"). */
  label?: string;
  /** Optional override for the default body copy. */
  fallbackMessage?: string;
  /** Optional callback fired when the user taps "Close" — typically used to
   *  dismiss the parent drawer/modal so the user is returned to the tree. */
  onReset?: () => void;
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * Lightweight render-error guard. Catches any synchronous render error in
 * its subtree and shows a non-blank fallback message instead of crashing
 * the whole page. Pure additive — does not touch tree relationships,
 * spouse links, parent/child edges, invites, Stripe, schema, or RLS.
 *
 * Used to wrap the profile drawer + media lightbox so a single bad
 * memory / photo URL / video element can never blank the entire app.
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(err: unknown): State {
    const message =
      err instanceof Error && err.message
        ? err.message
        : 'Something went wrong while opening this view.';
    return { hasError: true, message };
  }

  componentDidCatch(err: unknown, info: unknown) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label || '', err, info);
  }

  reset = () => {
    this.setState({ hasError: false, message: '' });
    this.props.onReset?.();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const label = this.props.label || 'this view';
    const body =
      this.props.fallbackMessage ||
      `We couldn't open ${label.toLowerCase()}. The item may be missing, private, or temporarily unavailable.`;

    return (
      <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
        <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-rose-500 to-amber-400 text-white p-5 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 shrink-0" />
            <div>
              <div className="font-serif font-bold text-lg">Couldn&apos;t open {label.toLowerCase()}</div>
              <div className="text-xs opacity-90">This memory could not be loaded.</div>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-slate-700">{body}</p>
            {this.state.message && (
              <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 break-words">
                {this.state.message}
              </p>
            )}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={this.reset}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold py-2.5 text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Close and go back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
